import React, { useEffect } from 'react'
import { Box, Divider, Tooltip, Zoom } from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'
import FormSwitch from '../../../../components/FormElements/FormSwitch'
import DefaultModalActions from '../../../../components/GenericModal/DefaultModalActions'
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined'
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined'
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined'
import { black, grey } from '../../../../colors'
import { useTheme } from '@mui/material/styles'
import { SurveyProjectType, type ISlot } from '../../../../models/DistributionPlan'
import { SurveyTypeBox } from './SurveyTypeBox'

interface SurveyProjectTypeAndRollingBasisToggleProps {
  handleCloseModal: () => void
  loading: boolean
  isCreating: boolean
  isSlotStatusLive: boolean
  onNext: () => void
}

export const SurveyProjectTypeAndRollingBasisToggle: React.FC<SurveyProjectTypeAndRollingBasisToggleProps> = ({
  handleCloseModal,
  loading,
  isCreating,
  isSlotStatusLive,
  onNext
}) => {
  const { control, setValue, getValues } = useFormContext()
  const theme = useTheme()

  const rollingTip = 'When enabled, results are released as soon as they’re processed—no need to wait for all results to be ready'

  useEffect(() => {
    if (!getValues('surveyProjectType')) {
      const projectType = getValues('isSlotAnonymous') === true
        ? SurveyProjectType.UNVERIFIED
        : SurveyProjectType.VERIFIED
      setValue('surveyProjectType', projectType, { shouldValidate: true, shouldDirty: true })
    }
  }, [getValues, setValue])

  return (
        <Box display="flex" flexDirection="column" gap={theme.spacing(2)} mt={theme.spacing(6)}>
            <Controller
                name="surveyProjectType"
                control={control}
                render={({ field }) => (
                    <Box display="flex" flexDirection="column" gap={theme.spacing(4)}>
                        <SurveyTypeBox
                            selected={field.value === SurveyProjectType.VERIFIED}
                            onClick={() => {
                              field.onChange(SurveyProjectType.VERIFIED)
                              setValue('isSlotAnonymous', false, { shouldValidate: true, shouldDirty: true })
                            }}
                            icon={<BadgeOutlinedIcon sx={{ fontSize: 48, color: black[100] }} />}
                            title="Verified Survey Project"
                            description="Only authenticated users assigned to course sections in the project can access and submit responses."
                            chipLabel="Recommended"
                        />
                        <SurveyTypeBox
                            selected={field.value === SurveyProjectType.UNVERIFIED}
                            onClick={() => {
                              field.onChange(SurveyProjectType.UNVERIFIED)
                              setValue('isSlotAnonymous', true, { shouldValidate: true, shouldDirty: true })
                            }}
                            icon={<PersonOffOutlinedIcon sx={{ fontSize: 48, color: black[100] }} />}
                            title="Unverified Survey Project"
                            description="Anyone with the link can submit responses to the course sections in the project—no login required."
                        />
                    </Box>
                )}
            />

            <Controller
                name="isRollingResultsEnabled"
                control={control}
                render={({ field }) => (
                    <Box display="flex" alignItems="center" gap={theme.spacing(1)} mt={theme.spacing(4)}>
                        <FormSwitch
                            {...field}
                            checked={field.value}
                            label="Release Results on a Rolling Basis"
                            disabled={isSlotStatusLive}
                            sx={{
                              '& .MuiFormControlLabel-label': {
                                color: black[100],
                                fontWeight: 500,
                                fontSize: '16px',
                                lineHeight: theme.spacing(6)
                              }
                            }}
                        />
                        <Tooltip
                            title={rollingTip}
                            placement="bottom"
                            arrow
                            TransitionComponent={Zoom}
                        >
                            <HelpOutlineOutlinedIcon sx={{ cursor: 'pointer', color: grey[300], mt: 0.5 }} />
                        </Tooltip>
                    </Box>
                )}
            />
            <Divider sx={{ borderColor: grey[200], mt: theme.spacing(4), width: '100%' }} />
            <DefaultModalActions
                destructiveButtonProps={{ onClick: handleCloseModal, disabled: loading || isCreating }}
                confirmButtonProps={{ text: 'Next', onClick: onNext }}
            />
        </Box>
  )
}
